import TagManager from "react-gtm-module";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import addAnalyticSanitize from "./functions/addAnalyticSanitize";
import { sha256 } from "./functions/sha256";
import { gtmId } from "./utils/consts/GtmId";
import { useScreenSize } from "@telefonica/mistica";
import type {
	Ecommerce,
	EcommerceItem,
	EventPurchase,
	ITagManager,
	Interaction,
	Page,
	Purchase,
	Sanitize,
} from "./utils/types";

interface IUseAnalytics {
	addAnalyticEvent(interaction: Interaction, sanitize: Sanitize): Promise<void>;
	updatePageView({ pagePath, name }: Omit<Page, "event" | "host" | "product">): Promise<void>;
	addAnalyticEventPurchase({ event, ecommerce, item, moreItems }: Purchase): Promise<void>;
}

export const useAnalytics = (): IUseAnalytics => {
	const { fintechRegisterStore } = useGlobalStore();
	const { isMobile } = useScreenSize();
	const cpf = fintechRegisterStore.personal.value?.cpfNumber;

	const addAnalyticEvent = async (
		{ component = "body", action, description, step = "", value = 0, event = "interaction" }: Interaction,
		{ applySanitize = true }: Sanitize = {},
	): Promise<void> => {
		const cripotografedCpf = cpf ? await sha256(cpf) : "";

		const tagManagerArgs: ITagManager<typeof event, Interaction> = {
			gtmId,
			dataLayer: {
				event,
				user: {
					cpf: cripotografedCpf,
				},
				interaction: {
					component,
					action,
					description: applySanitize ? addAnalyticSanitize(description) : description,
					step,
					value,
					product: "fgts",
				},
			},
		};

		TagManager.dataLayer(tagManagerArgs);
	};

	const addAnalyticEventPurchase = async ({ event, ecommerce, item, moreItems = [] }: Purchase): Promise<void> => {
		const cripotografedCpf = cpf ? await sha256(cpf) : "";

		const defaultItem: EcommerceItem = {
			item_id: "1234",
			item_brand: "vivo-money",
			item_category: "fgts",
			quantity: 1,
			index: 1,
			...item,
		};

		// PASSAR VALOR TOTAL E O VALOR ANTECIPADO

		const defaultEcommerce: Ecommerce = {
			currency: "BRL",
			payment_type: "pix",
			...ecommerce,
			items: [defaultItem, ...moreItems],
		};

		const tagManagerArgs: ITagManager<typeof event, EventPurchase> = {
			gtmId,
			dataLayer: {
				event,
				user: {
					cpf: cripotografedCpf,
				},
				ecommerce: defaultEcommerce,
			},
		};

		TagManager.dataLayer(tagManagerArgs);
	};

	const updatePageView = async ({
		pagePath,
		name,
		isNewPageviewFormat,
	}: Omit<Page, "host" | "product">): Promise<void> => {
		const criptografedCpf = cpf ? await sha256(cpf) : "";

		const isIphone = (): boolean | void => {
			if (typeof window === "undefined") {
				return;
			}
			return window.navigator.platform === "iPhone" ? true : false;
		};

		const pageView: Page = isNewPageviewFormat
			? {
				pagePath,
				area: "b2c",
				category: "vivo-pay",
				environment: isMobile ? "web" : "mobile",
				plataform: isMobile ? "web" : isIphone() ? "ios" : "android",
				segment: "ecommerce",
				subCategory: "vivo-pay",
			}
			: { pagePath, name, host: "vivomoney.com.br", product: "fgts" };
		const event = "pageView";

		const tagManagerArgs: ITagManager<typeof event, typeof pageView> = {
			gtmId,
			dataLayer: {
				event,
				page: pageView,
				user: {
					cpf: criptografedCpf,
				},
			},
		};

		TagManager.dataLayer(tagManagerArgs);
	};

	return {
		addAnalyticEvent,
		updatePageView,
		addAnalyticEventPurchase,
	};
};
